import classNames from 'classnames';
import { ChangeEvent, FC, ReactNode, useEffect, useId, useState } from 'react';
import Radio from '../Radio/Radio';
import { RadioProps } from '../Radio/Radio.types';
import styles from './RadioGroup.module.css';

interface RadioGroupProps {
  label: string;
  options: RadioProps[];
  selected?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  errorText?: string;
  disabled?: boolean;
  lockIcon?: ReactNode;
  horizontal?: boolean;
}

const RadioGroup: FC<RadioGroupProps> = ({
  options,
  label,
  selected,
  onChange,
  errorText,
  disabled,
  lockIcon,
  horizontal,
}) => {
  const id = useId();
  const [selectedOption, setSelectedOption] = useState(selected);

  useEffect(() => {
    setSelectedOption(selected);
  }, [selected]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event);
    if (event.target.ariaDisabled === 'true') return;
    setSelectedOption(event.target.id);
  };

  const renderOptions = options.map((radioProps) => (
    <Radio
      {...radioProps}
      name={id}
      key={radioProps.id}
      onChange={handleChange}
      checked={selectedOption === radioProps.id}
      disabled={disabled}
    />
  ));

  return (
    <fieldset>
      <legend className={styles.label}>
        {lockIcon}
        {label}
      </legend>
      <ul
        className={classNames(styles.optionList, {
          [styles.optionTable]: options.length > 2,
          [styles.horizontal]: horizontal,
        })}
        role="radiogroup"
      >
        {renderOptions}
      </ul>
      {errorText && (
        <div className={styles.errorText} aria-live="assertive" id={`${id}-error-text`}>
          {errorText}
        </div>
      )}
    </fieldset>
  );
};

export default RadioGroup;
